import { createApi } from '@reduxjs/toolkit/query/react'
import { BACKEND_API_URL, urlApi } from '../consts/api'
import { extractResponseData, extractResponseError, fetchBaseQueryWithToken } from '../utils/api'
import type { TeamDescription } from '../types/entitys/teams'
import { mapTeamFormToApiBody, normalizeTeamItem, normalizeTeamMemberResource, type TeamFormApiPayload } from './teamMappers'
import type { IProjectTeamMemberResource } from './types/projects'
import {
    parseProjectTeamInvitation,
    parseProjectTeamInvitationsList,
    parseProjectsCursorPageResponse,
    projectsApi,
    projectsTag,
} from './projects'
import type { IProjectTeamInvitationResource } from './types/projects'
import type { TProjectsCursorPage } from './types/projects'
import { notificationFeedScopeTags, notificationsApi, notificationsTag } from './notifications'
import { invalidateCachedTeamDetail, patchCachedGetTeamByTeamId } from './teamCacheUtils'
import type {
  TCreateTeamBody,
  TGetTeamsCursorQueryParams,
  TTeamsCursorPage,
  TTeamsListResponse,
  TTeamsStatsResponse,
  IUpdateTeamMembersDisplayBody,
  IUpdateTeamAdminsBody,
} from './types/teams'

export const teamsTag = 'teamsTag' as const

function parseTeamsListResponse(response: unknown): TTeamsListResponse {
  const top = (response ?? {}) as Record<string, unknown>
  const data = top.data

  if (Array.isArray(data)) {
    return data.map((item) => normalizeTeamItem(item as Record<string, unknown>))
  }

  if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>).data)) {
    return ((data as { data: unknown[] }).data).map((item) =>
      normalizeTeamItem(item as Record<string, unknown>),
    )
  }

  if (Array.isArray(response)) {
    return response.map((item) => normalizeTeamItem(item as Record<string, unknown>))
  }

  return []
}

function pickCursorFromNextLink(next: string): string | null {
  try {
    const fromUrl = new URL(next, BACKEND_API_URL).searchParams.get('cursor')
    if (fromUrl) {
      return fromUrl
    }
  } catch {
    // fall through
  }

  const match = next.match(/[?&]cursor=([^&]+)/)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

function pickNextCursorFromPageRoot(body: Record<string, unknown>): string | null {
  const meta = body.meta as Record<string, unknown> | undefined
  const fromMeta = meta?.next_cursor
  if (fromMeta !== undefined && fromMeta !== null && String(fromMeta) !== '') {
    return String(fromMeta)
  }

  const links = body.links as Record<string, unknown> | undefined
  const next = links?.next
  if (typeof next === 'string' && next.length > 0) {
    return pickCursorFromNextLink(next)
  }

  return null
}

function parseTeamsCursorPageResponse(response: unknown): TTeamsCursorPage {
  const top = (response ?? {}) as Record<string, unknown>

  return {
    items: parseTeamsListResponse(response),
    meta: { next_cursor: pickNextCursorFromPageRoot(top) },
  }
}

function serializeTeamsQueryParams(params: TGetTeamsCursorQueryParams | void): Record<string, string> {
  const raw = params ?? {}
  const result: Record<string, string> = {}

  const q = raw.query?.trim()
  if (q) {
    result.query = q
  }
  const city = raw.city?.trim()
  if (city) {
    result.city = city
  }
  const region = raw.region?.trim()
  if (region) {
    result.region = region
  }
  if (raw.sort_by) {
    result.sort_by = raw.sort_by
  }
  if (raw.sort_direction) {
    result.sort_direction = raw.sort_direction
  }
  if (raw.cursor) {
    result.cursor = raw.cursor
  }
  if (raw.limit != null) {
    result.limit = String(raw.limit)
  }

  return result
}

function teamListProvidesTags(
  result: TTeamsListResponse | TTeamsCursorPage | undefined,
): Array<{ type: typeof teamsTag; id: string }> {
  if (!result) {
    return [{ type: teamsTag, id: 'LIST' }]
  }
  const items = Array.isArray(result) ? result : result.items ?? []
  return [
    { type: teamsTag, id: 'LIST' },
    ...items.map((team) => ({ type: teamsTag, id: `id:${team.id}` })),
  ]
}

function teamInvalidationTags(teamId: number) {
  return [
    { type: teamsTag, id: 'LIST' },
    { type: teamsTag, id: `id:${teamId}` },
    { type: teamsTag, id: `members:${teamId}` },
    { type: teamsTag, id: 'STATS' },
  ]
}

export const teamsApi = createApi({
  reducerPath: 'teamsApi',
  baseQuery: fetchBaseQueryWithToken(BACKEND_API_URL),
  tagTypes: [teamsTag],
  endpoints: (builder) => ({
    getTeamsStats: builder.query<TTeamsStatsResponse, void>({
      query: () => ({
        url: `/${urlApi.teams}/stats`,
      }),
      transformResponse: (response: unknown) => {
        const data = extractResponseData(response) as Partial<TTeamsStatsResponse> | null
        return {
          teams_total_count: Number(data?.teams_total_count ?? 0),
          teams_published_count: Number(data?.teams_published_count ?? 0),
          teams_unpublished_count: Number(data?.teams_unpublished_count ?? 0),
        }
      },
      transformErrorResponse: extractResponseError,
      providesTags: [{ type: teamsTag, id: 'STATS' }],
    }),

    getTeamsPage: builder.query<TTeamsCursorPage, TGetTeamsCursorQueryParams | void>({
      query: (arg) => {
        const serialized = serializeTeamsQueryParams(arg)
        return {
          url: `/${urlApi.teams}`,
          ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
        }
      },
      transformResponse: (response: unknown) => parseTeamsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => teamListProvidesTags(result),
    }),

    getMyTeamsPage: builder.query<TTeamsCursorPage, TGetTeamsCursorQueryParams | void>({
      query: (arg) => {
        const serialized = serializeTeamsQueryParams(arg)
        return {
          url: `/${urlApi.teams}/mine`,
          ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
        }
      },
      transformResponse: (response: unknown) => parseTeamsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => teamListProvidesTags(result),
    }),

    getParticipatingTeamsPage: builder.query<TTeamsCursorPage, TGetTeamsCursorQueryParams | void>({
      query: (arg) => {
        const serialized = serializeTeamsQueryParams(arg)
        return {
          url: `/${urlApi.teams}/participating`,
          ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
        }
      },
      transformResponse: (response: unknown) => parseTeamsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => teamListProvidesTags(result),
    }),

    getUserTeamsPage: builder.query<TTeamsCursorPage, TGetTeamsCursorQueryParams & { userId: number }>({
      query: ({ userId, ...arg }) => {
        const serialized = serializeTeamsQueryParams(arg)
        return {
          url: `/${urlApi.users}/${userId}/teams`,
          ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
        }
      },
      transformResponse: (response: unknown) => parseTeamsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => teamListProvidesTags(result),
    }),

    getSubscribedTeamsPage: builder.query<TTeamsCursorPage, TGetTeamsCursorQueryParams | void>({
      query: (arg) => {
        const serialized = serializeTeamsQueryParams(arg)
        return {
          url: `/${urlApi.teams}/subscriptions`,
          ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
        }
      },
      transformResponse: (response: unknown) => parseTeamsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => teamListProvidesTags(result),
    }),

    searchTeams: builder.query<TTeamsListResponse, { query: string }>({
      query: ({ query }) => ({
        url: `/${urlApi.teams}`,
        params: {
          query: query.trim(),
          limit: String(10),
        },
      }),
      transformResponse: (response: unknown) => parseTeamsListResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => teamListProvidesTags(result),
    }),

    getTeam: builder.query<TeamDescription | null, { id: number | string }>({
      query: ({ id }) => ({
        url: `/${urlApi.teams}/${id}`,
      }),
      transformResponse: (response: unknown) => {
        if (!response || typeof response !== 'object') {
          return null
        }
        return normalizeTeamItem(response as Record<string, unknown>)
      },
      transformErrorResponse: extractResponseError,
      providesTags: (result) =>
        result
          ? [
              { type: teamsTag, id: `id:${result.id}` },
              { type: teamsTag, id: `members:${result.id}` },
            ]
          : [{ type: teamsTag, id: 'LIST' }],
    }),

    getTeamMembers: builder.query<IProjectTeamMemberResource[], { teamId: number }>({
      query: ({ teamId }) => ({
        url: `/${urlApi.teams}/${teamId}/members`,
      }),
      transformResponse: (response: unknown) => {
        const payload = extractResponseData(response)
        if (!Array.isArray(payload)) return []
        return payload
          .map((item) => normalizeTeamMemberResource(item))
          .filter((item): item is IProjectTeamMemberResource => item !== null)
      },
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [{ type: teamsTag, id: `members:${arg.teamId}` }],
    }),

    updateTeamMembersDisplay: builder.mutation<
      { updated: boolean },
      { teamId: number; body: IUpdateTeamMembersDisplayBody }
    >({
      query: ({ teamId, body }) => ({
        url: `/${urlApi.teams}/${teamId}/members-display`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => {
        const data = extractResponseData(response) as { updated?: boolean } | null
        return { updated: Boolean(data?.updated) }
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [
        { type: teamsTag, id: `members:${arg.teamId}` },
        { type: teamsTag, id: `id:${arg.teamId}` },
      ],
    }),

    updateTeamAdmins: builder.mutation<
      { admin_user_ids: number[] },
      { teamId: number; body: IUpdateTeamAdminsBody }
    >({
      query: ({ teamId, body }) => ({
        url: `/${urlApi.teams}/${teamId}/admins`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => {
        const data = extractResponseData(response) as { admin_user_ids?: number[] } | null
        const rawIds = data?.admin_user_ids
        const ids = Array.isArray(rawIds) ? rawIds.map((id) => Number(id)) : []
        return { admin_user_ids: ids }
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [
        { type: teamsTag, id: `members:${arg.teamId}` },
        { type: teamsTag, id: `id:${arg.teamId}` },
      ],
    }),

    removeTeamMember: builder.mutation<void, { teamId: number; userId: number }>({
      query: ({ teamId, userId }) => ({
        url: `/${urlApi.teams}/${teamId}/members/${userId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => teamInvalidationTags(arg.teamId),
    }),

    deleteTeam: builder.mutation<void, { teamId: number }>({
      query: ({ teamId }) => ({
        url: `/${urlApi.teams}/${teamId}/delete`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [
        { type: teamsTag, id: 'LIST' },
        { type: teamsTag, id: `id:${arg.teamId}` },
        { type: teamsTag, id: 'STATS' },
      ],
    }),

    getTeamProjectsPage: builder.query<
      TProjectsCursorPage,
      { teamId: number; cursor?: string | null; limit?: number }
    >({
      query: ({ teamId, cursor, limit }) => ({
        url: `/${urlApi.teams}/${teamId}/projects`,
        params: {
          ...(cursor ? { cursor } : {}),
          ...(limit != null ? { limit: String(limit) } : {}),
        },
      }),
      transformResponse: (response: unknown) => parseProjectsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [{ type: teamsTag, id: `projects:${arg.teamId}` }],
    }),

    createTeam: builder.mutation<{ id: number; slug: string }, { payload: TeamFormApiPayload }>({
      query: ({ payload }) => ({
        url: `/${urlApi.teams}`,
        method: 'POST',
        body: mapTeamFormToApiBody(payload) as TCreateTeamBody,
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeTeamItem((response ?? {}) as Record<string, unknown>)
        return { id: normalized.id, slug: String(normalized.slug ?? '') }
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: [{ type: teamsTag, id: 'LIST' }, { type: teamsTag, id: 'STATS' }],
    }),

    updateTeam: builder.mutation<TeamDescription, { id: number; payload: TeamFormApiPayload }>({
      query: ({ id, payload }) => ({
        url: `/${urlApi.teams}/${id}/update`,
        method: 'PUT',
        body: mapTeamFormToApiBody(payload),
      }),
      transformResponse: (response: unknown) =>
        normalizeTeamItem((response ?? {}) as Record<string, unknown>),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (result, _error, arg) => teamInvalidationTags(arg.id),
    }),

    trackTeamView: builder.mutation<{ views: number }, { teamId: number }>({
      query: ({ teamId }) => ({
        url: `/${urlApi.teams}/${teamId}/view`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => {
        const data = extractResponseData(response) as { views?: number } | null
        return { views: Number(data?.views ?? 0) }
      },
      transformErrorResponse: extractResponseError,
    }),

    subscribeTeam: builder.mutation<unknown, { teamId: number }>({
      query: ({ teamId }) => ({
        url: `/${urlApi.teams}/${teamId}/subscribe`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: [{ type: teamsTag, id: 'LIST' }, { type: teamsTag, id: 'STATS' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const patches = patchCachedGetTeamByTeamId(dispatch, getState, arg.teamId, (draft) => {
          draft.isSubscribed = true
          draft.subscribers = Number(draft.subscribers ?? 0) + 1
        })
        try {
          await queryFulfilled
        } catch {
          for (const patch of patches) {
            patch.undo()
          }
        }
      },
    }),

    unsubscribeTeam: builder.mutation<unknown, { teamId: number }>({
      query: ({ teamId }) => ({
        url: `/${urlApi.teams}/${teamId}/subscribe`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: [{ type: teamsTag, id: 'LIST' }, { type: teamsTag, id: 'STATS' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const patches = patchCachedGetTeamByTeamId(dispatch, getState, arg.teamId, (draft) => {
          draft.isSubscribed = false
          draft.subscribers = Math.max(0, Number(draft.subscribers ?? 0) - 1)
        })
        try {
          await queryFulfilled
        } catch {
          for (const patch of patches) {
            patch.undo()
          }
        }
      },
    }),

    leaveTeam: builder.mutation<void, { teamId: number; message?: string }>({
      query: ({ teamId, message }) => ({
        url: `/${urlApi.teams}/${teamId}/leave`,
        method: 'POST',
        body: message != null ? { message } : {},
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => teamInvalidationTags(arg.teamId),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const patches = patchCachedGetTeamByTeamId(dispatch, getState, arg.teamId, (draft) => {
          draft.isJoined = false
          draft.hasPendingJoinRequest = false
          draft.pendingJoinRequestId = undefined
          draft.participants = Math.max(0, Number(draft.participants ?? 0) - 1)
        })

        try {
          await queryFulfilled
        } catch {
          for (const patch of patches) {
            patch.undo()
          }
        }
      },
    }),

    uploadTeamCover: builder.mutation<unknown, { teamId: number; file: File }>({
      query: ({ teamId, file }) => {
        const formData = new FormData()
        formData.append('image', file)
        return {
          url: `/${urlApi.teams}/${teamId}/cover`,
          method: 'POST',
          body: formData,
        }
      },
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [{ type: teamsTag, id: `id:${arg.teamId}` }],
    }),

    deleteTeamCover: builder.mutation<TeamDescription, { teamId: number }>({
      query: ({ teamId }) => ({
        url: `/${urlApi.teams}/${teamId}/cover`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) =>
        normalizeTeamItem((response ?? {}) as Record<string, unknown>),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [{ type: teamsTag, id: `id:${arg.teamId}` }],
    }),

    getTeamProjectInvitations: builder.query<
      IProjectTeamInvitationResource[],
      { teamId: number; status?: string }
    >({
      query: ({ teamId, status }) => ({
        url: `/${urlApi.teams}/${teamId}/project-invitations`,
        params: status ? { status } : {},
      }),
      transformResponse: (response: unknown) => parseProjectTeamInvitationsList(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result, error, arg) => [
        { type: teamsTag, id: `project-invitations:${arg.teamId}` },
      ],
    }),

    acceptTeamProjectInvitation: builder.mutation<
      IProjectTeamInvitationResource,
      { teamId: number; invitationId: number; response_message?: string }
    >({
      query: ({ teamId, invitationId, response_message }) => ({
        url: `/${urlApi.teams}/${teamId}/project-invitations/${invitationId}/accept`,
        method: 'POST',
        body: response_message != null ? { response_message } : {},
      }),
      transformResponse: (response: unknown) => {
        const parsed = parseProjectTeamInvitation(extractResponseData(response))
        if (!parsed) {
          throw new Error('Invalid team invitation response')
        }
        return parsed
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: (result, error, arg) => [
        { type: teamsTag, id: `project-invitations:${arg.teamId}` },
        { type: teamsTag, id: `id:${arg.teamId}` },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            projectsApi.util.invalidateTags([
              { type: projectsTag, id: `id:${data.project_id}` },
              projectsTag,
            ]),
          )
          dispatch(
            notificationsApi.util.invalidateTags([
              ...notificationFeedScopeTags,
              { type: notificationsTag },
            ]),
          )
        } catch {
          // ignore
        }
      },
    }),

    declineTeamProjectInvitation: builder.mutation<
      IProjectTeamInvitationResource,
      { teamId: number; invitationId: number; response_message?: string }
    >({
      query: ({ teamId, invitationId, response_message }) => ({
        url: `/${urlApi.teams}/${teamId}/project-invitations/${invitationId}/decline`,
        method: 'POST',
        body: response_message != null ? { response_message } : {},
      }),
      transformResponse: (response: unknown) => {
        const parsed = parseProjectTeamInvitation(extractResponseData(response))
        if (!parsed) {
          throw new Error('Invalid team invitation response')
        }
        return parsed
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: (result, error, arg) => [
        { type: teamsTag, id: `project-invitations:${arg.teamId}` },
        { type: teamsTag, id: `id:${arg.teamId}` },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            notificationsApi.util.invalidateTags([
              ...notificationFeedScopeTags,
              { type: notificationsTag },
            ]),
          )
        } catch {
          // ignore
        }
      },
    }),

    getInvitableTeams: builder.query<TTeamsListResponse, void>({
      query: () => ({
        url: '/users/me/teams/invitable',
      }),
      transformResponse: (response: unknown) => parseTeamsListResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: [{ type: teamsTag, id: 'INVITABLE' }],
    }),

    createTeamInvitation: builder.mutation<
      unknown,
      { teamId: number; inviteeId: number; message?: string }
    >({
      query: ({ teamId, inviteeId, message }) => ({
        url: `/${urlApi.teams}/${teamId}/invitations`,
        method: 'POST',
        body: {
          invitee_id: inviteeId,
          ...(typeof message === 'string' && message.trim().length > 0 ? { message: message.trim() } : {}),
        },
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: [{ type: teamsTag, id: 'INVITABLE' }],
    }),

    createTeamJoinRequest: builder.mutation<unknown, { teamId: number; message?: string }>({
      query: ({ teamId, message }) => ({
        url: `/${urlApi.teams}/${teamId}/join-requests`,
        method: 'POST',
        ...(typeof message === 'string' && message.trim().length > 0 ? { body: { message: message.trim() } } : {}),
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled
          const joinRequestId = Number((data as Record<string, unknown> | null)?.id)
          patchCachedGetTeamByTeamId(dispatch, getState, arg.teamId, (draft) => {
            draft.hasPendingJoinRequest = true
            if (Number.isFinite(joinRequestId) && joinRequestId > 0) {
              draft.pendingJoinRequestId = joinRequestId
            }
          })
        } catch {
          // ignore
        }
      },
    }),

    approveTeamJoinRequest: builder.mutation<
      unknown,
      { teamId: number; joinRequestId: number; decision_message?: string }
    >({
      query: ({ teamId, joinRequestId, decision_message }) => ({
        url: `/${urlApi.teams}/${teamId}/join-requests/${joinRequestId}/approve`,
        method: 'POST',
        body: decision_message != null ? { decision_message } : {},
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => teamInvalidationTags(arg.teamId),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            notificationsApi.util.invalidateTags([
              ...notificationFeedScopeTags,
              { type: notificationsTag },
            ]),
          )
        } catch {
          // ignore
        }
      },
    }),

    rejectTeamJoinRequest: builder.mutation<
      unknown,
      { teamId: number; joinRequestId: number; decision_message?: string }
    >({
      query: ({ teamId, joinRequestId, decision_message }) => ({
        url: `/${urlApi.teams}/${teamId}/join-requests/${joinRequestId}/reject`,
        method: 'POST',
        body: decision_message != null ? { decision_message } : {},
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [{ type: teamsTag, id: `id:${arg.teamId}` }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            notificationsApi.util.invalidateTags([
              ...notificationFeedScopeTags,
              { type: notificationsTag },
            ]),
          )
        } catch {
          // ignore
        }
      },
    }),

    acceptTeamMemberInvitation: builder.mutation<
      unknown,
      { invitationId: number; response_message?: string }
    >({
      query: ({ invitationId, response_message }) => ({
        url: `/users/me/invitations/teams/${invitationId}/accept`,
        method: 'POST',
        body: response_message != null ? { response_message } : {},
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: [
        { type: teamsTag, id: 'LIST' },
        { type: teamsTag, id: 'INVITABLE' },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const teamId = Number((data as Record<string, unknown> | null)?.team_id)
          if (Number.isFinite(teamId) && teamId > 0) {
            invalidateCachedTeamDetail(dispatch, teamId)
          }
          dispatch(
            notificationsApi.util.invalidateTags([
              ...notificationFeedScopeTags,
              { type: notificationsTag },
            ]),
          )
        } catch {
          // ignore
        }
      },
    }),

    declineTeamMemberInvitation: builder.mutation<
      unknown,
      { invitationId: number; response_message?: string }
    >({
      query: ({ invitationId, response_message }) => ({
        url: `/users/me/invitations/teams/${invitationId}/decline`,
        method: 'POST',
        body: response_message != null ? { response_message } : {},
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: [teamsTag],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            notificationsApi.util.invalidateTags([
              ...notificationFeedScopeTags,
              { type: notificationsTag },
            ]),
          )
        } catch {
          // ignore
        }
      },
    }),
  }),
})

export const {
  useGetTeamsStatsQuery,
  useGetTeamsPageQuery,
  useLazyGetTeamsPageQuery,
  useLazyGetMyTeamsPageQuery,
  useLazyGetParticipatingTeamsPageQuery,
  useLazyGetUserTeamsPageQuery,
  useLazyGetSubscribedTeamsPageQuery,
  useSearchTeamsQuery,
  useLazySearchTeamsQuery,
  useGetTeamQuery,
  useLazyGetTeamQuery,
  useGetTeamMembersQuery,
  useUpdateTeamMembersDisplayMutation,
  useUpdateTeamAdminsMutation,
  useRemoveTeamMemberMutation,
  useDeleteTeamMutation,
  useLazyGetTeamProjectsPageQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useTrackTeamViewMutation,
  useSubscribeTeamMutation,
  useUnsubscribeTeamMutation,
  useLeaveTeamMutation,
  useUploadTeamCoverMutation,
  useDeleteTeamCoverMutation,
  useGetTeamProjectInvitationsQuery,
  useAcceptTeamProjectInvitationMutation,
  useDeclineTeamProjectInvitationMutation,
  useGetInvitableTeamsQuery,
  useCreateTeamInvitationMutation,
  useCreateTeamJoinRequestMutation,
  useApproveTeamJoinRequestMutation,
  useRejectTeamJoinRequestMutation,
  useAcceptTeamMemberInvitationMutation,
  useDeclineTeamMemberInvitationMutation,
} = teamsApi
